import { useEffect, useState } from 'react';
import { DataTableColumn, DataItem } from '../utils/types';

interface StickyOffsets {
  [index: number]: number;
}

interface HeaderRef {
  offsetWidth: number;
}

export const useStickyOffsets = (
  columns: DataTableColumn[],
  thRefs: React.MutableRefObject<(HTMLTableHeaderCellElement | null)[]>,
  thRefsAction: React.MutableRefObject<HTMLTableHeaderCellElement | null>,
  actionFixed: boolean,
  data: DataItem[],
): StickyOffsets => {
  const [stickyOffsets, setStickyOffsets] = useState<StickyOffsets>({});

  useEffect(() => {
    const calculateOffsets = () => {
      let leftOffset = 0;
      let rightOffset = 0;
      const actionWidth =
        actionFixed && thRefsAction.current
          ? thRefsAction.current.offsetWidth
          : 0;
      const newOffsets: StickyOffsets = {};

      columns.forEach((col, index) => {
        const headerRef = thRefs.current[index];
        if (!headerRef) return;

        if (col.fixed === 'left') {
          newOffsets[index] = leftOffset;
          leftOffset += headerRef.offsetWidth;
        } else if (col.fixed === 'right') {
          newOffsets[index] = rightOffset + actionWidth;
          rightOffset += headerRef.offsetWidth;
        }
      });

      setStickyOffsets(newOffsets);
    };

    calculateOffsets();

    // Add resize observer to handle window resizing
    const resizeObserver = new ResizeObserver(calculateOffsets);
    thRefs.current.forEach((ref) => {
      if (ref) resizeObserver.observe(ref);
    });
    if (thRefsAction.current) {
      resizeObserver.observe(thRefsAction.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [columns, data.length, actionFixed, thRefs, thRefsAction]);

  return stickyOffsets;
};
